import React, { useCallback, useEffect, useRef, useState } from "react";
import { Route, Switch } from "react-router-dom";


import { notify } from "@cayuse/shared-react.components.banner-notification";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import { useAuth } from "@cayuse/shared-react.utils.auth";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { ModelerPreview } from "containers/Form/Common/ModelerPreview";
import SubformNotification from "containers/Form/Common/SubformNotification";
import { FORMS_BASE_ROUTE } from "globals/constants";
import { useDictionary } from "hooks";
import { findElementAndSectionWithType, spUrl } from "utils";
import { request } from "utils/proxy";

import ModelerToolbar from "../Common/ModelerToolbar";
import { FLOWABLE_DESIGN_URL } from "../config";
import angularBootstrapConfig from "./AngularBootstrapConfig";
import angularHttpInterceptor from "./AngularHttpInterceptor";
import {angularRunFunction, angularRunContext } from "./AngularRunFunction";
import "./flowable-bootstrap.global.css";
import { SimpleExpressionModal } from "./SimpleExpressionModal";
import styles from "./styles.css";


/* global angular */
/* global _ */
/* global FLOWABLE */

const AdvancedBuilderView = ({
	history,
	match: {
		path,
		params: {
			formId: formIdParam,
			preview
		}
	}
}) => {
	const [ isFlowableLoaded, setIsFlowableLoaded ] = useState(undefined);
	const [ isFlowableLoadingError, setIsFlowableLoadingError ] = useState(false);
	const [ formModelId ] = useState(formIdParam);
	const [ previewFormDefinition, setPreviewFormDefinition ] = useState({});
	const [ angularDidMount, setAngularDidMount ] = useState(false);
	const [ isSaveBtnDisabled, setIsSaveBtnDisabled ] = useState(true);
	const [ modelKey, setModelKey ] = useState();
	const [ modelName, setModelName ] = useState();
	const { isAuthenticated } = useAuth();
	const containerRef = useRef(null);
	const $rootScopeContainer = useRef({});
	const notificationsDictionary = useDictionary("formDesigner.notifications");
	const isPreview = !!preview;

	const fetchModel = useCallback(formModelId => {
		const url = `${FLOWABLE_DESIGN_URL}/app/models?filter=apps&modelType=3&sort=modifiedDesc&wsId=${formModelId}`;

		return request(url);
	}, []);

	const [ isModelChecked, setIsModelChecked ] = useState(false);

	useEffect(() => {
		if (!isModelChecked) {
			setIsModelChecked(true);
			request(`${FLOWABLE_DESIGN_URL}/cayuse-api/simplified-modeler/models/${formModelId}`).then(model => {
				const element = findElementAndSectionWithType(model, "subform");
				if (element) {
					notify(
						<SubformNotification elementLabel={element.elementLabel} sectionLabel={element.sectionLabel}/>,
						{ type: "error", autoClose: false }
					);
				}
			});
		}
	}, [ formModelId, isModelChecked, notificationsDictionary ]);

	useEffect(() => {
		const unloadCallback = event => {
			if (!isSaveBtnDisabled) {
				event.preventDefault();
				event.returnValue = "";

				return "";
			}
		};

		window.addEventListener("beforeunload", unloadCallback);

		return () => window.removeEventListener("beforeunload", unloadCallback);
	}, [ isSaveBtnDisabled ]);

	const getInProgressDraft = () => $rootScopeContainer.current.eoVisFormPreviewService.getCurrentModelFormDefinition();

	const closePreview = () => {
		history.push(spUrl(`${FORMS_BASE_ROUTE}/${formModelId}`));
	};

	const clickMenu = className => {
		const button = document.getElementsByClassName(className)[0];

		if (button) {
			button.click();
		}
	};

	const getFormModelKey = name => {
		const formType = name.split(" ").pop().toLowerCase();

		switch(formType) {
		case "proposal":
			return "proposalForm";
		case "award":
			return "awardForm";
		case "subaward":
			return "subawardForm";
		default:
			return "";
		}
	};

	const bootstrapNgApp = useCallback(async () => {
		return fetchModel(formModelId)
			.then(appModelList => {
				const appModel = appModelList.data.find(app =>
					isNonEmptyArray(app.appDefinition.formModels) &&
					app.appDefinition.formModels.find(form => form.id === formModelId)
				);
				setModelKey(getFormModelKey(appModel.name));
				setModelName(appModel.name);
				angularRunContext.setContext({
					$rootScopeContainer: $rootScopeContainer.current,
					modelId: formModelId,
					appDefinition: appModel,
				});
				if (!angularRunContext.isAngularModuleConfigured()) {
					angularRunContext.markAngularModuleAsConfigured();
					angular.module("flowableModeler")
						.config(angularBootstrapConfig)
						.run(angularRunFunction)
						.factory("cayuseHttpInterceptor", angularHttpInterceptor);
				}
				angular.bootstrap(containerRef.current, [ "flowableModeler" ]);
			});
	}, [ formModelId, fetchModel, $rootScopeContainer ]);

	useEffect(() => {
		if (isAuthenticated && !angularDidMount && isFlowableLoaded && containerRef.current) {
			setAngularDidMount(true);

			bootstrapNgApp().catch(x => {
				notify("Unable to load the form modeler"); // todo: get proper copy and add to dictionary
			});
		}
	}, [ isFlowableLoaded, isAuthenticated, containerRef, formModelId, angularDidMount, bootstrapNgApp ]);

	useEffect(() => {
		let intervalId;
		if (isPreview) {
			let attempts = 20;

			const checkFormDefinitionChange = async () => {
				attempts--;
				if ($rootScopeContainer.current?.eoVisFormPreviewService) {
					const previousFormDefinition = previewFormDefinition;
					const newFormDefinition = await getInProgressDraft();
					if (!_.isEqual(previousFormDefinition, newFormDefinition)) {
						setPreviewFormDefinition(_.cloneDeep(newFormDefinition));
						if (Object.keys(newFormDefinition).length > 0) {
							clearInterval(intervalId);
						}
					}
				}
				if (attempts < 1) {
					clearInterval(intervalId);
				}
			};

			intervalId = setInterval(checkFormDefinitionChange, 500);
		}

		return () => intervalId && clearInterval(intervalId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ isPreview ]);

	useEffect(() => {
		const maxTimeInMs = 30000;
		const intervalInMs = 20;
		let interval;
		let attemptsLeft = maxTimeInMs / intervalInMs;
		const $rsContainer = $rootScopeContainer.current;

		const checkIsFlowableLoaded = () => {
			if (window.FLOWABLE_LOADED === true) {
				FLOWABLE.CONFIG.appDefaultRoute = spUrl(FORMS_BASE_ROUTE);
				FLOWABLE.CONFIG.contextRoot = `${FLOWABLE_DESIGN_URL}`;

				setIsFlowableLoaded(true);
				clearInterval(interval);
			} else {
				attemptsLeft--;
			}

			if (!attemptsLeft) {
				console.warn("Unable to load Flowable", window.FLOWABLE);
				setIsFlowableLoadingError(true);
				clearInterval(interval);
			}
		};

		interval = setInterval(checkIsFlowableLoaded, intervalInMs);

		return () => {
			clearInterval(interval);
			delete $rsContainer.$rootScope;
			delete $rsContainer.eoVisFormPreviewService;
			// Clear the Angular run context to prevent stale references if the user navigates away from the form builder and then back again
			angularRunContext.clearContext();
		};
	}, []);

	useEffect(() => {
		const $rsContainer = $rootScopeContainer.current;

		return () => {
			// Clean up AngularJS scopes to prevent memory leaks and errors from stale scope references after unmounting
			if ($rsContainer?.$rootScope) {
				$rsContainer.$rootScope.$destroy();
			}
		};
	}, []);

	if (window.location.pathname !== history.location.pathname) {
		if (isPreview) {
			history.replace(spUrl(`${FORMS_BASE_ROUTE}/${formModelId}/preview`));
		}
		else {
			history.replace(spUrl(`${FORMS_BASE_ROUTE}/${formModelId}`));
		}
	}

	return (
		<PaddedCell>
			{isFlowableLoaded && <SimpleExpressionModal getFormDefinition={getInProgressDraft} />}

			{isFlowableLoaded
				&& (
					<>
						<Switch>
							<Route
								exact
								path={`${path}/preview`}
								render={() => Object.keys(previewFormDefinition).length > 0
									? <ModelerPreview config={previewFormDefinition} closePreview={closePreview} />
									: <LoadingIndicator/>
								}
							/>

							<Route
								exact
								path={path}
							>
								<>
									<div className={styles.menuButtonContainer}>
										<SectionHeader header={modelName || "Form"} />
										<ModelerToolbar
											getInProgressDraft={getInProgressDraft}
											saveDraft={() => clickMenu("eo-vis-menu-save-all")}
											history={history}
											formId={formModelId}
											isSaveBtnDisabled={isSaveBtnDisabled}
											setIsSaveBtnDisabled={setIsSaveBtnDisabled}
											modelKey={modelKey}
										/>
									</div>
								</>
							</Route>
						</Switch>
						<div
							className={isPreview ? styles.hidden : ""}
							ref={containerRef}
							dangerouslySetInnerHTML={{
								__html: "<div class=\"clearfix noPadding flowable " + styles.flowable +
									" eo-vis-editor-pane-container\"><eo-vis-editor /></div>",
							}}
						/>
					</>
				)
			}

			{isFlowableLoadingError && (
				<p>Unable to load the form builder within the allotted time frame. This can happen due to internet
					connection speed and/or size of the form. Please reload the page and try again</p>)}
			{!isFlowableLoaded && !isFlowableLoadingError && <LoadingIndicator/>}
		</PaddedCell>
	);
};

export default AdvancedBuilderView;
